import moment from "moment";

/**
 * Catálogo de disciplinas + cores pastéis (rotação gera efeito mosaico na semana).
 * `icon`: nome Material Icons; `iconColor`: cor do ícone e da faixa esquerda (contraste com o pastel).
 */
const DISCIPLINE_SLOTS = [
  {
    title: "Matemática",
    place: "Sala 201",
    sub: "Funções",
    color: "#bfede3",
    icon: "grade",
    iconColor: "#0d9488"
  },
  {
    title: "Língua Portuguesa",
    place: "Sala 203",
    sub: "Leitura",
    color: "#d3e4fa",
    icon: "menu_book",
    iconColor: "#2563eb"
  },
  {
    title: "Ciências",
    place: "Laboratório",
    sub: "Química",
    color: "#ffe8cc",
    icon: "opacity",
    iconColor: "#ea580c"
  },
  {
    title: "História",
    place: "Sala 105",
    sub: "Brasil colonial",
    color: "#c5ebe4",
    icon: "library_books",
    iconColor: "#047857"
  },
  {
    title: "Geografia",
    place: "Sala 112",
    sub: "Climatologia",
    color: "#d9e8ff",
    icon: "public",
    iconColor: "#1d4ed8"
  },
  {
    title: "Inglês",
    place: "Sala 208",
    sub: "Conversation",
    color: "#fde6d2",
    icon: "translate",
    iconColor: "#c2410c"
  },
  {
    title: "Arte",
    place: "Atelier",
    sub: "Projeto cores",
    color: "#ebe0f5",
    icon: "palette",
    iconColor: "#7c3aed"
  },
  {
    title: "Educação Física",
    place: "Quadra",
    sub: "Vôlei",
    color: "#e0f4e8",
    icon: "directions_run",
    iconColor: "#15803d"
  },
  {
    title: "Filosofia",
    place: "Sala 301",
    sub: "Ética",
    color: "#f0e6ff",
    icon: "forum",
    iconColor: "#6d28d9"
  },
  {
    title: "Robótica",
    place: "Lab maker",
    sub: "Arduino",
    color: "#fff4d6",
    icon: "build",
    iconColor: "#ca8a04"
  },
  {
    title: "Projeto integrador",
    place: "Sala multifuncional",
    sub: "Grupos A/B",
    color: "#dff2fb",
    icon: "group_work",
    iconColor: "#0369a1"
  },
  {
    title: "Reforço escolar",
    place: "Sala 14",
    sub: "Acompanhamento",
    color: "#fce4ec",
    icon: "school",
    iconColor: "#be185d"
  }
];

/** Faixas horárias em UTC: [hIni, minIni, hFim, minFim] — preenchimento contínuo tipo grade escolar */
const DAY_GRID_BASE = [
  [7, 25, 8, 40],
  [8, 55, 10, 15],
  [10, 30, 11, 50],
  [13, 0, 14, 20],
  [14, 35, 15, 55],
  [16, 10, 17, 25],
  [17, 40, 19, 0],
  [19, 15, 20, 25]
];

const TURMAS = ["DEMO-T1", "DEMO-T2", "DEMO-T3"];

function pickDiscipline(dayIndex, slotIndex) {
  const i = (dayIndex * 5 + slotIndex * 2) % DISCIPLINE_SLOTS.length;
  return DISCIPLINE_SLOTS[i];
}

/**
 * Varia levemente os horários conforme o dia (evita “cópia” monótona na semana).
 */
function shiftMinutes(dayIndex, slotIndex) {
  return ((dayIndex + slotIndex * 3) % 5) * 3;
}

function buildEventsForOneUtcDay(dayUtc, dayIndex) {
  const add = (m, mins) => m.clone().add(mins, "minutes");

  /** Alguns dias têm menos aulas (mosaico menos denso) */
  const useShortDay = dayIndex % 4 === 3;
  const rows = useShortDay ? DAY_GRID_BASE.slice(0, 5) : DAY_GRID_BASE;

  return rows.map((row, slotIndex) => {
    const d = pickDiscipline(dayIndex, slotIndex);
    const turma = TURMAS[(dayIndex + slotIndex) % TURMAS.length];
    const shift = shiftMinutes(dayIndex, slotIndex);

    let start = dayUtc
      .clone()
      .hour(row[0])
      .minute(row[1])
      .second(0)
      .millisecond(0);
    let end = dayUtc
      .clone()
      .hour(row[2])
      .minute(row[3])
      .second(0)
      .millisecond(0);
    start = add(start, shift);
    end = add(end, shift);

    if (!end.isAfter(start)) {
      end = start.clone().add(50, "minutes");
    }

    const line2 = `${turma} · ${d.sub}`;
    const title = `${d.title} - ${d.place}`;
    const slotKey = `${start.valueOf()}-${end.valueOf()}-${turma}`;

    return {
      title,
      description: line2,
      start: start.toISOString(),
      end: end.toISOString(),
      color: d.color,
      textColor: "#1a3a45",
      extendedProps: {
        description: line2,
        icon: d.icon,
        iconColor: d.iconColor,
        classId: turma,
        slotKey
      }
    };
  });
}

/**
 * Horários demo para GET Academics/teacher/schedule (Home e tela Horários).
 * Cobre **todo o intervalo** [start, end) pedido pelo FullCalendar (dia, semana, etc.),
 * com disciplinas e cores diferentes por dia → efeito mosaico na vista semanal.
 *
 * @param {string} [startStr] ISO (início do intervalo)
 * @param {string} [endStr] ISO (**exclusivo**, como no FullCalendar)
 */
export function buildDemoTeacherSchedule(startStr, endStr) {
  if (!startStr) return [];

  const start = moment.utc(startStr);
  if (!start.isValid()) return [];

  const end = endStr
    ? moment.utc(endStr)
    : start.clone().add(1, "day");

  if (!end.isValid() || !end.isAfter(start)) {
    return [];
  }

  const out = [];

  /**
   * end é exclusivo (FullCalendar): emitimos um “dia cheio” de demo para cada
   * data UTC em que existe interseção com [start, end).
   * Usar `while (day.isBefore(end))` evita erros de `startOf(end-1ms)` que zeravam a lista.
   */
  const day = start.clone().startOf("day");
  let dayIndex = 0;
  const maxDays = 60;

  while (day.isBefore(end) && dayIndex < maxDays) {
    out.push(...buildEventsForOneUtcDay(day.clone(), dayIndex));
    dayIndex += 1;
    day.add(1, "day");
  }

  if (out.length === 0 && start.isBefore(end)) {
    return buildEventsForOneUtcDay(start.clone().startOf("day"), 0);
  }

  return out;
}

/**
 * Eventos demo de um dia (UTC), mesmo algoritmo que o calendário / atalhos de horário.
 * @param {string} dayStr YYYY-MM-DD
 * @param {number} [dayIndex=0] Índice do dia no intervalo (afeta turnos leves e disciplina).
 * @returns {Array<{ start: string, end: string, extendedProps: { classId: string, slotKey: string } }>}
 */
export function getDemoScheduleEventsForDayKey(dayStr, dayIndex = 0) {
  const dayUtc = moment.utc(dayStr, "YYYY-MM-DD", true);
  if (!dayUtc.isValid()) {
    return [];
  }
  return buildEventsForOneUtcDay(dayUtc.clone().startOf("day"), dayIndex);
}
